import type { Permission } from '@kortex/shared';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth, type SubscriptionSummary } from '../lib/auth';
import { initials, titleCase } from '../lib/format';
import { useToast } from './ui';
import { ChangePasswordModal } from '../pages/Password';
import { TwoFactorModal } from '../pages/TwoFactor';
import { Assistant, HELP_SLOT_ID } from './Assistant';
import { NotificationBell } from './Notifications';

interface NavEntry {
  to: string;
  label: string;
  icon: string;
  permission: Permission;
  group: string;
}

const NAV: NavEntry[] = [
  { group: 'Operations', to: '/', label: 'Dashboard', icon: '◈', permission: 'dashboard:view' },
  { group: 'Operations', to: '/pos', label: 'POS Billing', icon: '▤', permission: 'pos:operate' },
  {
    group: 'Operations',
    to: '/online-orders',
    label: 'Online Orders',
    icon: '⇲',
    permission: 'pos:operate',
  },
  {
    group: 'Operations',
    to: '/kitchen',
    label: 'Kitchen Display',
    icon: '♨',
    permission: 'kds:view',
  },
  {
    group: 'Operations',
    to: '/front-desk',
    label: 'Front Desk',
    icon: '⌂',
    permission: 'rooms:view',
  },
  {
    group: 'Operations',
    to: '/reservations',
    label: 'Reservations',
    icon: '◷',
    permission: 'reservation:view',
  },
  {
    group: 'Operations',
    to: '/housekeeping',
    label: 'Housekeeping',
    icon: '✦',
    permission: 'housekeeping:view',
  },
  {
    group: 'Operations',
    to: '/invoices',
    label: 'Invoices',
    icon: '❑',
    permission: 'invoice:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/menu',
    label: 'Menu & Pricing',
    icon: '☰',
    permission: 'menu:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/offers',
    label: 'Offers',
    icon: '٪',
    permission: 'menu:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/inventory',
    label: 'Inventory',
    icon: '⛁',
    permission: 'inventory:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/procurement',
    label: 'Procurement',
    icon: '⇄',
    permission: 'procurement:view',
  },
  { group: 'People', to: '/people', label: 'Staff & Payroll', icon: '☺', permission: 'hr:view' },
  { group: 'Insight', to: '/reports', label: 'Reports', icon: '◱', permission: 'reports:view' },
  { group: 'Insight', to: '/gst', label: 'GST Filing', icon: '₹', permission: 'gst:export' },
  {
    group: 'Administration',
    to: '/payments',
    label: 'Payments',
    icon: '₹',
    permission: 'settings:manage',
  },
  {
    group: 'Administration',
    to: '/users',
    label: 'Users & Roles',
    icon: '⚿',
    permission: 'users:view',
  },
  {
    group: 'Administration',
    to: '/settings',
    label: 'Settings',
    icon: '⚙',
    /* Reading the property is something the floor screens do; opening the
       Administration screen is the owner's business. */
    permission: 'settings:manage',
  },
  {
    group: 'Administration',
    to: '/audit',
    label: 'Audit Log',
    icon: '≡',
    permission: 'audit:view',
  },
];

const PLATFORM_NAV = [
  { to: '/platform', label: 'Platform', icon: '◉' },
  { to: '/platform/hotels', label: 'Hotels', icon: '⌂' },
  { to: '/platform/plans', label: 'Plans & Pricing', icon: '₹' },
  { to: '/platform/reports', label: 'Platform Reports', icon: '◱' },
  { to: '/platform/mail', label: 'Outgoing Mail', icon: '✉' },
  { to: '/platform/captcha', label: 'Sign-in Challenge', icon: '🛡' },
  { to: '/platform/errors', label: 'Server Errors', icon: '⚠' },
];

/** Where "Talk to us" sends a hotelier about their subscription. */
const SUPPORT_URL = 'https://www.europiumsolutions.com/contact-us/';

/**
 * Europium Solutions links, shown on every screen including the ones you reach
 * before signing in.
 *
 * Each opens in a new tab so a cashier mid-ticket does not lose the till, and
 * each carries noopener/noreferrer for the same reason the "Talk to us" button
 * does: the new tab must not reach back through window.opener.
 */
const FOOTER_LINKS = [
  { label: 'About Us', href: 'https://www.europiumsolutions.com/about-us/' },
  { label: 'Our Portfolio', href: 'https://www.europiumsolutions.com/portfolio/' },
  { label: 'Contact Us', href: SUPPORT_URL },
  /* Ours, not europiumsolutions.com, so it opens the version the app records
     acceptance against. */
  { label: 'Terms & Conditions', href: '/terms' },
] as const;

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <nav>
        {FOOTER_LINKS.map((link) => (
          <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">
            {link.label}
          </a>
        ))}
      </nav>
      <div className="footer-right">
        <span>© {new Date().getFullYear()} Europium Solutions</span>
        {/* Filled by the assistant where one is mounted; empty everywhere else. */}
        <span id={HELP_SLOT_ID} className="footer-help" />
      </div>
    </footer>
  );
}

export function Layout() {
  const [changingPassword, setChangingPassword] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);
  const { session, signOut, switchProperty, can, isPlatformAdmin, subscription } = useAuth();
  const { fail } = useToast();
  const location = useLocation();
  const [propertyMenu, setPropertyMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // Following a link on a phone should reveal the page, not leave the drawer
  // sitting over it.
  useEffect(() => setNavOpen(false), [location.pathname]);
  const [theme, setTheme] = useState(() => localStorage.getItem('kortex-theme') ?? 'light');
  const propertyRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('kortex-theme', theme);
  }, [theme]);

  useEffect(() => {
    setNavOpen(false);
    setPropertyMenu(false);
    setUserMenu(false);
  }, [location.pathname]);

  // Close whichever popover is open when the click lands outside it.
  useEffect(() => {
    if (!propertyMenu && !userMenu) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (propertyMenu && !propertyRef.current?.contains(target)) setPropertyMenu(false);
      if (userMenu && !userRef.current?.contains(target)) setUserMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [propertyMenu, userMenu]);

  if (!session) return null;

  const visible = NAV.filter((entry) => can(entry.permission));
  const groups = [...new Set(visible.map((entry) => entry.group))];
  const current = visible.find((entry) => entry.to === location.pathname);

  const onSwitch = async (propertyId: string) => {
    try {
      await switchProperty(propertyId);
    } catch (error) {
      fail(error);
    }
  };

  return (
    <div className="app">
      <aside className={`sidebar no-print ${navOpen ? 'open' : ''}`}>
        <div className="brand">
          <div className="brand-mark">K</div>
          <div>
            <div className="brand-name">Kortex HMS</div>
            <div className="brand-sub">{session.property?.code ?? 'Platform'}</div>
          </div>
        </div>

        {!session.property ? (
          <div className="switcher" style={{ cursor: 'default' }}>
            <div>
              <div className="sw-name">Kortex Platform</div>
              <div className="sw-role">Superadmin</div>
            </div>
          </div>
        ) : session.properties.length > 1 ? (
          <div style={{ position: 'relative' }} ref={propertyRef}>
            <button className="switcher" onClick={() => setPropertyMenu((open) => !open)}>
              <div>
                <div className="sw-name">{session.property.name}</div>
                <div className="sw-role">{titleCase(session.role ?? '')}</div>
              </div>
              <span className="sw-caret">⌄</span>
            </button>
            {propertyMenu ? (
              <div className="menu-pop" style={{ left: 10, right: 10, top: 'calc(100% + 2px)' }}>
                <div className="head">Switch property</div>
                {session.properties.map((property) => (
                  <button key={property.id} onClick={() => void onSwitch(property.id)}>
                    <span>{property.name}</span>
                    <span className="xs muted" style={{ marginLeft: 'auto' }}>
                      {titleCase(property.role)}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="switcher" style={{ cursor: 'default' }}>
            <div>
              <div className="sw-name">{session.property.name}</div>
              <div className="sw-role">{titleCase(session.role ?? '')}</div>
            </div>
          </div>
        )}

        <nav className="nav">
          {isPlatformAdmin ? (
            <div>
              <div className="nav-group">Kortex Platform</div>
              {PLATFORM_NAV.map((entry) => (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.to === '/platform'}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  <span className="ico">{entry.icon}</span>
                  <span>{entry.label}</span>
                </NavLink>
              ))}
            </div>
          ) : null}
          {groups.map((group) => (
            <div key={group}>
              <div className="nav-group">{group}</div>
              {visible
                .filter((entry) => entry.group === group)
                .map((entry) => (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    end={entry.to === '/'}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  >
                    <span className="ico">{entry.icon}</span>
                    <span>{entry.label}</span>
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="row between">
            <span>{session.property?.gstin ?? 'Platform console'}</span>
            <button
              className="btn ghost sm"
              style={{ color: '#9fc4c0' }}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title="Toggle theme"
            >
              ◐
            </button>
          </div>
        </div>
      </aside>

      {navOpen ? (
        <button
          className="nav-scrim no-print"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <div className="main">
        <header className="topbar no-print">
          <button
            className="btn ghost icon sm nav-toggle"
            onClick={() => setNavOpen((open) => !open)}
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={navOpen}
          >
            ☰
          </button>
          <div>
            <div className="title">{current?.label ?? 'Kortex HMS'}</div>
            <div className="crumb">{session.property?.name ?? 'Kortex Platform'}</div>
          </div>
          <div className="grow" />
          <NotificationBell />
          <div style={{ position: 'relative' }} ref={userRef}>
            <button className="user-chip" onClick={() => setUserMenu((open) => !open)}>
              <span className="avatar">{initials(session.user.name)}</span>
              <span className="small b">{session.user.name}</span>
              <span className="muted">⌄</span>
            </button>
            {userMenu ? (
              <div className="menu-pop">
                <div className="head">{session.user.email}</div>
                <div className="sep" />
                <button
                  onClick={() => {
                    setUserMenu(false);
                    setChangingPassword(true);
                  }}
                >
                  Change password
                </button>
                <button
                  onClick={() => {
                    setUserMenu(false);
                    setTwoFactor(true);
                  }}
                >
                  Two-step verification
                </button>
                <button onClick={() => void signOut()}>Sign out</button>
              </div>
            ) : null}
          </div>
        </header>

        <main className="view">
          {subscription && subscription.status !== 'ACTIVE' && subscription.isEntitled ? (
            <div className="sub-banner">
              <span className="chip">{subscription.planName}</span>
              <span>
                {subscription.status === 'TRIALING'
                  ? subscription.daysRemaining === 0
                    ? 'Your free trial ends today.'
                    : `Your free trial has ${subscription.daysRemaining} ${subscription.daysRemaining === 1 ? 'day' : 'days'} left.`
                  : `Your subscription is ${subscription.statusLabel.toLowerCase()}.`}
              </span>
              {can('settings:manage') ? (
                /* noopener stops the new tab reaching back through window.opener;
                   noreferrer withholds the URL it was opened from. */
                <a className="btn sm" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                  Talk to us
                </a>
              ) : null}
            </div>
          ) : null}

          {subscription && !subscription.isEntitled ? (
            <SubscriptionEnded subscription={subscription} isOwner={session?.role === 'OWNER'} />
          ) : (
            <Outlet />
          )}
        </main>

        <SiteFooter />
      </div>

      <Assistant />

      {changingPassword ? <ChangePasswordModal onClose={() => setChangingPassword(false)} /> : null}
      {twoFactor ? <TwoFactorModal onClose={() => setTwoFactor(false)} /> : null}
    </div>
  );
}

/**
 * What a hotel sees once its subscription has ended.
 *
 * Not a banner over a screen that cannot load: every API call answers 402 at
 * this point, so the screens behind would be a wall of errors. The owner is
 * the only person who can do anything about it, so they get the two things
 * that help - renew, or talk to us - and everybody else gets told plainly
 * whose job it is, rather than being left to wonder what they broke.
 */
function SubscriptionEnded({
  subscription,
  isOwner,
}: {
  subscription: SubscriptionSummary;
  isOwner: boolean;
}) {
  return (
    <div className="ended-shell">
      <div className="card ended-card">
        <div className="card-body col gap-16">
          <div className="row gap-6 wrap">
            <span className="chip danger">{subscription.statusLabel}</span>
            <span className="chip">{subscription.planName}</span>
          </div>

          <h2>{isOwner ? 'Your plan has expired' : 'Your current plan has expired'}</h2>

          {isOwner ? (
            <>
              <p className="small">
                {subscription.planName} ended
                {subscription.endsAt
                  ? ` on ${new Date(subscription.endsAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}`
                  : ''}
                . Your data is safe and nothing has been deleted — the hotel is closed for writing
                until the plan is renewed.
              </p>
              <div className="row gap-6 wrap">
                <a
                  className="btn primary"
                  href={SUPPORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Upgrade my plan
                </a>
                <a className="btn" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                  Talk to us
                </a>
              </div>
              <p className="xs muted">
                Once it is renewed, sign out and back in — or just refresh — and everything is where
                you left it.
              </p>
            </>
          ) : (
            <>
              <p className="small">
                Your current plan has expired, reach out to owner for upgrade.
              </p>
              <p className="xs muted">
                Nothing has been lost. The hotel is closed for writing until the owner renews it.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="view-head">
      <div>
        <h1>{title}</h1>
        {subtitle ? <div className="sub">{subtitle}</div> : null}
      </div>
      {actions ? <div className="row gap-6">{actions}</div> : null}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="page-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={tab.id === active ? 'active' : ''}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
